import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import useHighlightParam from "@/hooks/useHighlightParam";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { ContactDeleteDialog } from "@/components/ContactDeleteDialog";
import { ZefixExportButton } from "@/components/ZefixExportButton";
import { ZefixEnrichButton, ZefixStatusBadge, toastEnriched } from "@/components/ZefixEnrichButton";
import { CreditLight, isEuCountry } from "@/components/customers/CreditLight";

export function Customers() {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    postal_code: "",
    city: "",
    vat_id: "",
    iban: "",
    country: "CH",
  });
  const [scores, setScores] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const companyId = localStorage.getItem("demoCompanyId");

  const loadCustomers = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/contacts`, {
        params: { type: "customer" },
      });
      setCustomers(res.data || []);
      apiClient.get(`/companies/${companyId}/customers/credit-scores`).then((r) => { setScores(r.data?.scores || {}); setSummary(r.data?.summary || null); }).catch(() => {});
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Kunden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useHighlightParam(customers, (c) => c._id || c.id);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) {
      setError("Kein Mandant gefunden. Bitte Seite neu laden.");
      return;
    }
    if (!form.name.trim()) {
      setError("Name ist ein Pflichtfeld.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        type: "customer",
        name: form.name.trim(),
        email: form.email || null,
        address: form.address || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        vat_id: form.vat_id || null,
        iban: form.iban ? form.iban.replace(/\s+/g, "").toUpperCase() : null,
        country: (form.country || "CH").trim().toUpperCase() || "CH",
      };
      const res = await apiClient.post(`/companies/${companyId}/contacts`, payload);
      toastEnriched(payload, res.data);
      setCustomers((prev) => [...prev, res.data]);
      setForm({ name: "", email: "", address: "", postal_code: "", city: "", vat_id: "", iban: "", country: "CH" });
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Anlegen des Kunden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Kunden</h1>
          <p className="mt-1 text-sm text-slate-500">
            Verwalten Sie Ihre Kundenstammdaten für Ausgangsrechnungen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ZefixEnrichButton companyId={companyId} onDone={() => loadCustomers()} testId="customers-hr-enrich-btn" />
          <ZefixExportButton
            url={`/companies/${companyId}/zefix/export-contacts`}
            body={{ contact_type: "customer" }}
            label="Handelsregister-Abgleich (CSV)"
            testId="customers-hr-export-btn"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-xl border border-slate-200 bg-white shadow-sm" data-testid="customers-form-card">
        <form className="space-y-3" onSubmit={handleCreate} data-testid="customers-form">
          <div className="space-y-1">
            <Label htmlFor="customer-name">Name</Label>
            <Input
              id="customer-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="customers-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="customer-address">Adresse</Label>
            <Input
              id="customer-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="customers-address-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="customer-postal">PLZ</Label>
              <Input
                id="customer-postal"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                data-testid="customers-postal-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="customer-city">Ort</Label>
              <Input
                id="customer-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="customers-city-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="customer-vat">MWST-/UID-Nr.</Label>
              <Input
                id="customer-vat"
                value={form.vat_id}
                onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
                data-testid="customers-vat-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_90px] gap-3">
            <div className="space-y-1">
              <Label htmlFor="customer-email">E-Mail</Label>
              <Input
                id="customer-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="customers-email-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="customer-iban">IBAN (für LSV / Lastschrift)</Label>
              <Input
                id="customer-iban"
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                placeholder="CH00 0000 0000 0000 0000 0"
                data-testid="customers-iban-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="customer-country">Land</Label>
              <Input
                id="customer-country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="CH"
                maxLength={2}
                data-testid="customers-country-input"
              />
            </div>
          </div>
          {isEuCountry(form.country) && (
            <p className="text-[11px] text-sky-700" data-testid="customers-eu-hint">EU-Kunde: Rechnungen können als Factur-X/ZUGFeRD (E-Rechnung) erzeugt werden.</p>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="customers-save-button"
            >
              {saving ? "Speichern ..." : "Kunden anlegen"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="customers-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Alle Kunden</h2>
          {summary && (
            <div className="flex items-center gap-3 text-[11px] text-slate-500" data-testid="customers-credit-summary">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />{summary.green} zuverlässig</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" />{summary.yellow} verzögert</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500" />{summary.red} Risiko</span>
            </div>
          )}
          {loading && (
            <span className="text-xs text-slate-400" data-testid="customers-loading">
              Lade ...
            </span>
          )}
        </div>
        <Table data-testid="customers-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Adresse</TableHead>
              <TableHead>PLZ / Ort</TableHead>
              <TableHead>E-Mail</TableHead>
              <TableHead>MWST-/UID-Nr.</TableHead>
              <TableHead>Zahlungsverhalten</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    testid="customers-empty-state"
                    icon={<Users className="h-5 w-5" />}
                    title="Noch keine Kunden"
                    description="Legen Sie Ihren ersten Kunden an, um Rechnungen mit Swiss QR zu erstellen."
                  />
                </TableCell>
              </TableRow>
            )}
            {customers.map((c) => (
              <TableRow key={c._id} data-testid={`customers-row-${c._id}`} data-highlight-id={c._id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.address}</TableCell>
                <TableCell>
                  {c.postal_code} {c.city}
                </TableCell>
                <TableCell>{c.email}</TableCell>
                <TableCell>{c.vat_id}<ZefixStatusBadge contact={c} /></TableCell>
                <TableCell>
                  <CreditLight score={scores[c._id] || { light: "unknown", reasons: ["Noch keine Rechnung"] }} testId={`customers-credit-${c._id}`} />
                  {isEuCountry(c.country) && <span className="ml-1 text-[10px] text-sky-700" data-testid={`customers-eu-${c._id}`}>EU · Factur-X</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    onClick={() => setDeleteTarget(c)}
                    data-testid={`customers-delete-button-${c._id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Löschen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ContactDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        companyId={companyId}
        contact={deleteTarget}
        kind="Kunde"
        onDeleted={(id) => setCustomers((prev) => prev.filter((x) => x._id !== id))}
      />
    </div>
  );
}
